/**
 * OnboardingWizard - Top-level step router for the onboarding flow.
 * Manages state via useOnboardingSession hook and renders the appropriate step.
 *
 * URL: /login = email + OTP only. /onboarding = after verification (path, datasets, setup).
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import {
  useOnboardingSession,
  completedSignupWizardHomePath,
  type DatasetId,
  type ChosenPath,
  type OnboardingStep,
} from './hooks/useOnboardingSession';
import SignUpStep from './steps/SignUpStep';
import VerifyOTPStep from './steps/VerifyOTPStep';
import ChoosePathStep from './steps/ChoosePathStep';
import SampleDataStep from './steps/SampleDataStep';
import SetupProgressStep from './steps/SetupProgressStep';
import OwnDataBridgeStep from './steps/OwnDataBridgeStep';
import {
  getTenantIdFromStorage,
  removeOnboardingPathAndDataset,
  removeOnboardingDataset,
  setOnboardingDataset,
  setOnboardingPath,
  setOnboardingTtvClock,
} from '../../utils/onboardingLocalStorage';
import {
  coarseErrorMessage,
  trackOnboardingOwnDataBridgeContinue,
  trackOnboardingOwnDataTrySampleInstead,
  trackOnboardingPathBack,
  trackOnboardingPathSelected,
  trackOnboardingSampleDatasetSelected,
  trackOnboardingSampleProvisionFailed,
  trackOnboardingSampleProvisionSucceeded,
  trackOnboardingSetupProgressCompleted,
} from '../../utils/analytics';
import { getToken, isTokenExpired } from '../../utils/auth';

const PRE_OTP_STEPS = new Set<OnboardingStep>(['signup', 'otp_sent']);
const POST_OTP_ONBOARDING_STEPS = new Set<OnboardingStep>([
  'otp_verified',
  'path_chosen',
  'sample_dataset_picked',
]);

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useOnboardingSession();
  const [provisionPromise, setProvisionPromise] = useState<Promise<any> | null>(null);
  const [showOwnDataBridge, setShowOwnDataBridge] = useState(false);
  const [pathBackLoading, setPathBackLoading] = useState(false);
  /** After "back" from sample or own-data bridge, which path to highlight on ChoosePathStep */
  const [pathChoicePrefill, setPathChoicePrefill] = useState<ChosenPath | null>(null);

  // ── Step handlers ──

  const handleSignup = useCallback(async (email: string) => {
    try { await session.signup(email); } catch { /* displayed via session.error */ }
  }, [session]);

  const handleVerifyOtp = useCallback(async (otpCode: string) => {
    try {
      const data = await session.verifyOtp(otpCode);
      const step = (data?.current_step as OnboardingStep) || 'otp_verified';
      if (step === 'completed') {
        navigate(completedSignupWizardHomePath(data?.chosen_path), { replace: true });
        return;
      }
      navigate('/onboarding', { replace: true });
    } catch { /* displayed via session.error */ }
  }, [session, navigate]);

  const handleResendOtp = useCallback(async () => {
    try { return await session.resendOtp(); } catch { /* displayed via session.error */ }
  }, [session]);

  const handleChoosePath = useCallback(async (path: ChosenPath) => {
    setPathChoicePrefill(null);
    if (path === 'own_data') {
      const tid = session.tenantId || getTenantIdFromStorage();
      try { setOnboardingPath(tid, 'own_data'); } catch { /* ignore */ }
      try { removeOnboardingDataset(tid); } catch { /* ignore */ }
      // Show the bridge screen first to set expectations before entering the product.
      try {
        await session.updateStep('path_chosen', { chosen_path: path });
        trackOnboardingPathSelected({ chosen_path: 'own_data' });
      } catch { /* displayed via session.error */ }
      setShowOwnDataBridge(true);
      return;
    }
    setShowOwnDataBridge(false);
    try {
      setOnboardingPath(session.tenantId || getTenantIdFromStorage(), 'sample');
    } catch { /* ignore */ }
    try {
      await session.updateStep('path_chosen', { chosen_path: path });
      trackOnboardingPathSelected({ chosen_path: 'sample' });
    } catch { /* displayed via session.error */ }
  }, [session]);

  /** Return to sample vs own-data choice (updates server step + clears path fields). */
  const handleBackToPathChoice = useCallback(async (prefill: ChosenPath | null = null) => {
    setShowOwnDataBridge(false);
    setPathChoicePrefill(prefill);
    try {
      removeOnboardingPathAndDataset(session.tenantId || getTenantIdFromStorage());
    } catch { /* ignore */ }
    setPathBackLoading(true);
    try {
      await session.updateStep('otp_verified', {});
      trackOnboardingPathBack({ prefill_path: prefill });
    } catch { /* displayed via session.error */ }
    finally {
      setPathBackLoading(false);
    }
  }, [session]);

  const handleOwnDataBridgeContinue = useCallback(async () => {
    try {
      await session.updateStep('completed', {});
      trackOnboardingOwnDataBridgeContinue();
      navigate('/connectors', { replace: true });
    } catch { /* displayed via session.error */ }
  }, [session, navigate]);

  const DATASET_DISPLAY_NAMES: Record<DatasetId, string> = {
    ecommerce: 'E-Commerce & Sales',
    marketing: 'Marketing & Campaigns',
    supply_chain: 'Supply Chain & Ops',
  };

  const handleSelectSampleDataset = useCallback(async (datasetId: DatasetId) => {
    try {
      await session.updateStep('sample_dataset_picked', { chosen_dataset: datasetId });
      trackOnboardingSampleDatasetSelected({ dataset_id: datasetId });
    } catch { /* displayed via session.error */ }
    // Store dataset name for the completion modal time-to-value display
    const tid = session.tenantId || getTenantIdFromStorage();
    setOnboardingDataset(tid, DATASET_DISPLAY_NAMES[datasetId] ?? datasetId);
    // Reset TTV clock when committing to a sample dataset (repeat testing / long pause after OTP)
    setOnboardingTtvClock(tid);
    const promise = session.provisionSample(datasetId);
    void promise
      .then(() => trackOnboardingSampleProvisionSucceeded({ dataset_id: datasetId }))
      .catch((err: unknown) =>
        trackOnboardingSampleProvisionFailed({ dataset_id: datasetId, error_type: coarseErrorMessage(err) }),
      );
    setProvisionPromise(promise);
  }, [session]);

  const handleSetupComplete = useCallback(() => {
    trackOnboardingSetupProgressCompleted({ dataset_id: session.chosenDataset });
    navigate('/copilot', { replace: true });
  }, [navigate, session.chosenDataset]);

  // ── Render the current step ──

  const { currentStep, chosenPath, sessionHydrated } = session;

  // Keep URL aligned: /login only before OTP; in-product onboarding steps use /onboarding
  useEffect(() => {
    if (!sessionHydrated) return;
    if (currentStep === 'completed') return;

    const path = location.pathname;
    const onOnboardingPath = path === '/onboarding' || path.startsWith('/onboarding/');

    if (path === '/login' && POST_OTP_ONBOARDING_STEPS.has(currentStep)) {
      navigate('/onboarding', { replace: true });
    } else if (onOnboardingPath && PRE_OTP_STEPS.has(currentStep)) {
      // After verify-otp, navigate('/onboarding') runs before React applies currentStep
      // (e.g. otp_verified). For one frame we still have otp_sent + JWT — do not force /login
      // or the user bounces back to the OTP screen with tokens already in localStorage.
      const token = getToken();
      if (token && !isTokenExpired(token)) {
        return;
      }
      navigate('/login', { replace: true });
    }
  }, [sessionHydrated, currentStep, location.pathname, navigate]);

  /** Do not navigate during render — React 18 can drop it. Navigate after OTP when step is `completed`. */
  useEffect(() => {
    if (!sessionHydrated || currentStep !== 'completed') return;
    navigate(completedSignupWizardHomePath(session.chosenPath), { replace: true });
  }, [sessionHydrated, currentStep, session.chosenPath, navigate]);

  if (!sessionHydrated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (currentStep === 'signup' || currentStep === 'otp_sent') {
    if (currentStep === 'otp_sent' && session.email) {
      return (
        <VerifyOTPStep
          email={session.email}
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
          onBackToEmail={session.backToEmailStep}
          loading={session.loading}
          error={session.error}
        />
      );
    }
    return (
      <SignUpStep
        onSubmit={handleSignup}
        loading={session.loading}
        error={session.error}
      />
    );
  }

  if (currentStep === 'otp_verified') {
    return (
      <ChoosePathStep
        onChoose={handleChoosePath}
        defaultSelectedPath={pathChoicePrefill ?? 'sample'}
        loading={session.loading}
        error={session.error}
      />
    );
  }

  if (showOwnDataBridge || (currentStep === 'path_chosen' && chosenPath === 'own_data')) {
    return (
      <OwnDataBridgeStep
        onContinue={handleOwnDataBridgeContinue}
        onTrySampleInstead={() => {
          trackOnboardingOwnDataTrySampleInstead();
          void handleBackToPathChoice('sample');
        }}
        backNavLoading={pathBackLoading || session.loading}
        loading={session.loading}
      />
    );
  }

  if (currentStep === 'path_chosen' && chosenPath === 'sample') {
    return (
      <SampleDataStep
        onSelect={handleSelectSampleDataset}
        onBackToPathChoice={handleBackToPathChoice}
        backNavLoading={pathBackLoading}
        loading={session.loading}
        error={session.error}
      />
    );
  }

  if (currentStep === 'sample_dataset_picked') {
    return (
      <SetupProgressStep
        onComplete={handleSetupComplete}
        provisionPromise={provisionPromise || undefined}
      />
    );
  }

  if (currentStep === 'completed') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Default: show signup
  return (
    <SignUpStep
      onSubmit={handleSignup}
      loading={session.loading}
      error={session.error}
    />
  );
}
